import {
  ChangeEvent,
  FormEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Flex,
  Form,
  FormContext,
  FormProvider,
  Text,
  ViewerContext,
  toast,
  useServerResponseTranslation,
  localStorage,
} from 'dash-ui';

import { FormFields, FormFieldsList } from './ChangeEmail.constants';
import useValidation from './ChangeEmail.hooks';
import { useUpdateEmailMutation } from 'gql/generated.graphql';

function ChangeEmailForm() {
  const { t } = useTranslation(['pages', 'forms']);
  const formRef = useRef<HTMLFormElement>(null);

  const { account } = useContext(ViewerContext);
  const {
    formState,
    stateHandlers,
    formErrors,
    resetForm,
    resetFormErrors,
    setDefaultFormValues,
  } = useContext(FormContext);

  const validate = useValidation();

  const [showPasswordConfirmation, setShowPasswordConfirmation] =
    useState<boolean>(false);
  const translateErrorMessage = useServerResponseTranslation();

  const [updateEmail] = useUpdateEmailMutation({
    update(cache: any) {
      cache.modify({
        id: cache.identify({
          id: account?._id,
          __typename: 'Account',
        }),
        fields: {
          email: () => formState.email,
        },
      });
    },
  });

  useEffect(() => {
    if (account) {
      setDefaultFormValues({
        email: account.email,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  useEffect(() => {
    if (account?.email !== formState.email && !showPasswordConfirmation) {
      setShowPasswordConfirmation(true);
    }
    if (account?.email === formState.email && showPasswordConfirmation) {
      setShowPasswordConfirmation(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, formState.email]);

  const onFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.stopPropagation();
    e.preventDefault();

    updateEmail({
      variables: {
        newEmail: formState.email,
        password: formState.password,
      },
      onCompleted: ({ updateEmail: token }) => {
        const { accessToken, refreshToken, refreshTokenExpiry } = token;
        localStorage.setAccessToken(accessToken);
        localStorage.setRefreshToken(refreshToken);
        localStorage.setRefreshTokenExpiry(refreshTokenExpiry || '');
        window.dispatchEvent(new Event('storage'));
        toast.success(t('pages:profilePage.toasts.updatedEmail'));
      },
      onError: (error: any) => {
        toast.error(translateErrorMessage(error));
      },
    });
  };

  const onInputChange =
    (fieldName: FormFields) => (e: ChangeEvent<HTMLInputElement>) => {
      stateHandlers[fieldName](e.target.value);
      validate(fieldName, e.target.value);
    };

  return (
    <Form w="100%" ref={formRef} onSubmit={onFormSubmit}>
      <Flex flexDirection="column" w={635}>
        <Form.Group mt={4} w="100%">
          <Form.Label md required>
            {t('forms:labels.email')}
          </Form.Label>
          <Form.Input
            required
            placeholder={t('forms:labels.email') || ''}
            w="100%"
            value={formState.email}
            onChange={onInputChange(FormFields.Email)}
            $errorType={formErrors[FormFields.Email]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Email]?.type}>
            {formErrors[FormFields.Email]?.message}
          </Form.Feedback>
        </Form.Group>
        {showPasswordConfirmation && (
          <Form.Group mt={4} w="100%">
            <Form.Label md required>
              {t('forms:labels.password')}
            </Form.Label>
            <Form.Input
              required
              type="password"
              placeholder={t('forms:labels.password') || ''}
              value={formState.password}
              onChange={onInputChange(FormFields.Password)}
              w="100%"
              $errorType={formErrors[FormFields.Password]?.type}
            />
            <Form.Feedback $errorType={formErrors[FormFields.Password]?.type}>
              {formErrors[FormFields.Password]?.message}
            </Form.Feedback>
          </Form.Group>
        )}
      </Flex>
      <Flex w={224} mt={4} justifyContent="space-between">
        <Button
          w={108}
          type="button"
          disabled={account?.email === formState.email}
          onClick={() => {
            if (formRef.current) {
              formRef.current.requestSubmit();
            }
          }}
        >
          {t('common:labels.change')}
        </Button>
        <Button
          w={108}
          variation="outline"
          type="reset"
          onClick={() => {
            resetForm();
            resetFormErrors();
          }}
        >
          {t('common:labels.cancel')}
        </Button>
      </Flex>
    </Form>
  );
}

function ChangeEmail() {
  const { t } = useTranslation(['pages']);

  return (
    <Flex flexDirection="column" mt={8}>
      <Text semibold>{t('pages:profilePage.settings.updateEmailTitle')}</Text>
      <FormProvider fieldsList={FormFieldsList}>
        <ChangeEmailForm />
      </FormProvider>
    </Flex>
  );
}

export default ChangeEmail;
